/** R-16 T-135: GET/PUT /api/me/quick-links/{appSlug} — the reader's own quick links, kept on the server per app. */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export interface QuickLinksAnswer {
  app: string;
  ids: string[];
  /** PR #88: the list as kept, including pages the reader's tier hides now; a save starts from this */
  stored: string[];
  source: 'user' | 'role' | 'default';
  defaults: string[];
  rowMax: number;
}

export const quickLinksKey = (app: string) => ['cigs', 'quick-links', app] as const;

export function useQuickLinks(app: string | null) {
  return useQuery<QuickLinksAnswer>({
    queryKey: quickLinksKey(app ?? ''),
    queryFn: () => apiFetch<QuickLinksAnswer>(`/me/quick-links/${encodeURIComponent(app!)}`),
    enabled: Boolean(app),
    staleTime: 60_000,
  });
}

/**
 * PUT one list (null = back to the defaults) and keep the server's answer as the cached read. The provider calls it
 * through a SaveQueue (model.ts): one save at a time, each built from the latest list (PR #88 round 2).
 */
export function useSendQuickLinks(app: string | null) {
  const qc = useQueryClient();
  return useCallback(
    async (ids: string[] | null) => {
      const answer = await apiFetch<QuickLinksAnswer>(`/me/quick-links/${encodeURIComponent(app!)}`, { method: 'PUT', body: { ids } });
      qc.setQueryData(quickLinksKey(app ?? ''), answer);
      return answer.stored;
    },
    [app, qc],
  );
}
